import React, { Suspense, lazy } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { Loader2 } from "lucide-react";
import { AuthProvider } from "./context/AuthContext";
import ProtectedRoute from "./components/ProtectedRoute";
import AuthCallback from "./components/AuthCallback";

// Eagerly load landing + dashboard (most visited routes) 
const LandingPage = lazy(() => import(/* webpackPreload: true */ "./pages/LandingPage"));
const EnergyDashboard = lazy(() => import(/* webpackPreload: true */ "./pages/EnergyDashboard"));
const QuickAssessment = lazy(() => import("./pages/QuickAssessment"));
const DailyQuickAssessment = lazy(() => import("./pages/DailyQuickAssessment"));
const DataSources = lazy(() => import("./pages/DataSources"));
const MealSuggestions = lazy(() => import("./pages/MealSuggestions"));
const ProfilePage = lazy(() => import("./pages/ProfilePage"));
const SubscriptionPage = lazy(() => import("./pages/SubscriptionPage"));

// Keep old pages for backwards compatibility (can be removed later)
const MealPlanner = lazy(() => import("./pages/MealPlanner"));
const WeeklyPlanner = lazy(() => import("./pages/WeeklyPlanner"));
const HistoryPage = lazy(() => import("./pages/HistoryPage"));
const RecipeDetailPage = lazy(() => import("./pages/RecipeDetailPage"));
const AdminPage = lazy(() => import("./pages/AdminPage"));
const EnergyReportPage = lazy(() => import("./pages/EnergyReportPage"));
const EnergyBlueprintQuiz = lazy(() => import("./pages/EnergyBlueprintQuiz"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const NotificationsPage = lazy(() => import("./pages/NotificationsPage"));

const PageLoader = () => (
  <div className="mobile-container flex items-center justify-center min-h-screen bg-stone-900">
    <Loader2 className="w-12 h-12 text-amber-500 animate-spin" />
  </div>
);

const AppRouter = () => {
  const location = useLocation();

  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        
        {/* New Energy Tracking Routes */}
        <Route path="/dashboard" element={<ProtectedRoute><EnergyDashboard /></ProtectedRoute>} />
        <Route path="/assessment" element={<ProtectedRoute><QuickAssessment /></ProtectedRoute>} />
        <Route path="/daily-check" element={<ProtectedRoute><DailyQuickAssessment /></ProtectedRoute>} />
        <Route path="/data-sources" element={<ProtectedRoute><DataSources /></ProtectedRoute>} />
        <Route path="/meals" element={<ProtectedRoute><MealSuggestions /></ProtectedRoute>} />
        
        {/* Profile & Settings */}
        <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
        <Route path="/settings" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
        <Route path="/subscription" element={<ProtectedRoute><SubscriptionPage /></ProtectedRoute>} />
        
        {/* Legacy routes (kept for backwards compatibility) */}
        <Route path="/planner" element={<ProtectedRoute><MealPlanner /></ProtectedRoute>} />
        <Route path="/weekly" element={<ProtectedRoute><WeeklyPlanner /></ProtectedRoute>} />
        <Route path="/history" element={<ProtectedRoute><HistoryPage /></ProtectedRoute>} />
        <Route path="/recipe/:id" element={<ProtectedRoute><RecipeDetailPage /></ProtectedRoute>} />
        <Route path="/admin" element={<ProtectedRoute><AdminPage /></ProtectedRoute>} />
        <Route path="/energy-report" element={<ProtectedRoute><EnergyReportPage /></ProtectedRoute>} />
        <Route path="/energy-blueprint" element={<ProtectedRoute><EnergyBlueprintQuiz /></ProtectedRoute>} />
        <Route path="/notifications" element={<ProtectedRoute><NotificationsPage /></ProtectedRoute>} />
      </Routes>
    </Suspense>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster position="top-center" richColors />
        <AppRouter />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
